/**
 * PlanAssignmentsTable — every reading plan assignment: the plan, who it was
 * given to, when it starts and whether it is on. Used as a tab on the admin
 * screen (all assignments) and inline under one plan (`planId` set).
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Trash2, Church, Globe2, CalendarDays } from 'lucide-react';

const ICONS = { church: Church, province: Globe2, event: CalendarDays };

export const usePlanAssignments = (planId) => useQuery({
  queryKey: ['bible-plan-assignments', planId || 'all'],
  queryFn: async () => (await api.get('/admin/bible/plan-assignments', {
    params: planId ? { plan_id: planId } : {},
  })).data,
});

const Row = ({ row, showPlan }) => {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['bible-plan-assignments'] });
  const Icon = ICONS[row.target_type] || Church;

  const toggle = useMutation({
    mutationFn: async (next) => (await api.patch(
      `/admin/bible/plan-assignments/${row.id}`, { is_active: next })).data,
    onSuccess: (r) => { toast.success(r.is_active ? 'Assignment switched on' : 'Assignment switched off'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/admin/bible/plan-assignments/${row.id}`)).data,
    onSuccess: () => { toast.success('Assignment removed — nobody loses their progress'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="flex flex-wrap items-center gap-3 py-3"
         data-testid={`assignment-row-${row.id}`}>
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        {showPlan && (
          <p className="text-sm font-medium text-slate-900 truncate"
             data-testid={`assignment-plan-${row.id}`}>{row.plan_title}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700"
             data-testid={`assignment-target-${row.id}`}>
          <span className="truncate">{row.target_name}</span>
          <Badge variant="outline" className="text-[10px] capitalize">
            {String(row.target_level || row.target_type).replace(/_/g, ' ')}
          </Badge>
          {row.target_type === 'province' && (
            <span className="text-[11px] text-slate-500">and every church beneath</span>
          )}
        </div>
      </div>
      <div className="text-xs text-slate-500 w-28 shrink-0" data-testid={`assignment-start-${row.id}`}>
        {row.started ? 'Started' : 'Starts'} {row.start_date}
      </div>
      <div className="text-xs text-slate-500 w-32 shrink-0" data-testid={`assignment-reach-${row.id}`}>
        {row.enrolled} of {row.reach} reading
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Switch checked={!!row.is_active} disabled={toggle.isPending}
                onCheckedChange={(v) => toggle.mutate(v)}
                data-testid={`assignment-active-${row.id}`} />
        <span className="text-xs text-slate-500 w-12">{row.is_active ? 'Active' : 'Off'}</span>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600"
                disabled={remove.isPending}
                onClick={() => { if (window.confirm(`Remove this plan from ${row.target_name}?`)) remove.mutate(); }}
                data-testid={`assignment-remove-${row.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export const PlanAssignmentsTable = ({ planId, showPlan = true }) => {
  const { data, isLoading } = usePlanAssignments(planId);
  const rows = data?.assignments || [];

  if (isLoading) {
    return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  }
  if (rows.length === 0) {
    return (
      <Card data-testid="assignments-empty">
        <CardContent className="py-8 text-center text-sm text-slate-500">
          Nothing assigned yet. Open a plan and choose <strong>Assign</strong> to give it to a
          church, a campus, a whole province or an event.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="divide-y py-1" data-testid="assignments-table">
        {rows.map((row) => <Row key={row.id} row={row} showPlan={showPlan} />)}
      </CardContent>
    </Card>
  );
};

export default PlanAssignmentsTable;
